import {
  DeliveryMethods,
  ExternalDriverStatusEnum,
  OrderTransactionPrices,
  Sources,
} from "@/shared/utils/enum";
import { CartItemStatus, Deposit } from "@/shared/utils/interfaces";

export interface SingleOrderResponseV2 {
  order: OrderV2;
  geolocations: Geolocation[];
}

export interface PickupOrder {
  item_id: string;
  receipt_number: string;
  order_status: string;
  order_user?: {
    name?: string;
    first_name?: string;
    last_name?: string;
  };
  created_at?: string;
}

export interface CourierReport {
  name: string;
  account_id: number;
  start_date_and_time: string;
  end_date_and_time: string;
  break_in_minutes: string;
  total_cash_orders: number;
  total_orders: number;
  money_at_start_of_day: number;
  time_stamp: string;
  item_id: number;
  partial_deposit: Deposit;
  owed_to_merchant: number;
  orders: OrderV2[];
  orders_per_payment_method: {
    [key: number]: {
      count: number;
      value: number;
    };
  };
}

export interface PersonnelCheckInPatchResponse {
  account_id: string;
  break_in_minutes: string;
  item_id: number;
  money_at_start_of_day: string;
  start_date_and_time: string;
  time_stamp: string;
}

export interface OrderV2 {
  amount?: number;
  order_information: OrderInformation;
  order_user: OrderUser;
  cart_content: CartContent[];
}

export interface OrderInformation {
  discount_code_value?: string;
  order_color?: string;
  table_id?: number;
  table_number?: number;
  desired_hand_over_time: string;
  order_session_id?: number;
  receipt_print_retry_count?: number;
  order_status_history: { [key: number]: string };
  assigned_courier_id_time_stamp: string;
  daily_receipt_number: string;
  external_order_id: string;
  total_paid: string;
  zipcode_color_code: string;
  public_order_number: string;
  remarks: string;
  placed_date_time: string;
  hand_over_date: string;
  hand_over_time: string;
  hand_over_is_asap: boolean;
  payment_method_id: number;
  assigned_courier_id_timestamp: string;
  delivery_method_id: number;
  total_price: string;
  subtotal_prices: SubtotalPrices;
  order_user_id: number;
  source: number;
  ip_address: string;
  order_status: number;
  branch_id: number;
  order_read_secret_key: string;
  order_write_secret_key: string;
  order_feedback_secret_key: string;
  cart_item_usage_ids: number[];
  assigned_courier_id: number;
  hand_over_opening_time_date: string;
  placed_date_opening_time_date: string;
  loyalty_ledger_points: string;
  tip: string;
  order_payment_status: number;
  distance_to_branch_in_km: string;
  hand_over_start_time: string;
  hand_over_end_time: string;
  vat_prices: VatPrices;
  receipt_printer_printed_datetime: { [key: string]: string };
  item_id: number;
  is_custom_time_slot_selected?: boolean;
  custom_time_slot_label?: string;
  external_driver_status?: ExternalDriverStatusEnum;
  external_driver_status_timestamp?: string;
}

export interface SubtotalPrices {
  "1": string;
  "2": string;
  "3": string;
}

export interface VatPrices {
  "1": string;
  "2": string;
  "3": string;
}

export interface GeoCoordinates {
  "2": string; //lat
  "3": string; //lon
  "4": string; // degree
  "5": string; //compass heading
}

export interface OrderUser {
  name: string;
  area: string;
  street_name: string;
  house_number: string;
  house_number_extension: string;
  zip_code: string;
  city: string;
  telephone: string;
  email: string;
  status: number;
  hash_crc32: number;
  item_id: number;
  website_account_id?: number;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  user_note?: string;
  address_geo_coordinates?: GeoCoordinates;
}

export interface CartContent {
  cart_usage_item_id: number;
  kitchen_end_time: string;
  kitchen_screen_id: number;
  kitchen_start_time: string;
  kitchen_status: CartItemStatus;
  type: string;
  name: string;
  code: string;
  vat_percentage: string;
  single_price_inc_vat: string;
  receipt_name: { [key: string]: string };
  parent_name: string;
  menu_item_id: string;
  parent_id: string;
  quantity?: string;
  note?: string;
  toppings: any[];
  is_for_another_screen?: boolean;
}

export interface Geolocation {
  order_id: string;
  account_id: string;
  lat: string;
  lon: string;
  time_stamp: string;
}

export type OrdersLive = OrderV2[][];

export interface CourierGeoLocation {
  account_name: string;
  account_id: string;
  lat: string;
  lon: string;
  order: OrderV2 | "0";
  time_stamp: string;
  order_id: string;
  speed: string;
  ride_id: string;
}

export type Modify<T, R> = Omit<T, keyof R> & R;
export enum MenuItemTypes {
  "Category" = 1,
  "Product",
  "Topping Category",
  "Topping",
}
export interface MenuInformation {
  item_id: number;
  slider_button_action_target: string;
  slider_button_action_type: string;
  slider_button_text: string;
  slider_media_source: string;
  slider_media_type_enum: string;
}

export interface Menu {
  information: Partial<MenuInformation>;
  navigation_content: Category[];
}

export interface MenuItem<T = MenuItemTypes> {
  item_type: T;
  name: string;
  item_id: number;
  children: MenuItem[];
  // sub_categories: MenuItem[];
}

export interface Topping extends Modify<
  MenuItem<"4">,
  {
    children: undefined;
    menu_item_id?: string;
    single_price_inc_vat?: string;
  }
> {
  price: string;
  image_cloudinary_id?: string;
}

export interface ToppingCategory extends Modify<
  MenuItem<"3">,
  {
    sub_content: Topping[];
  }
> {
  image_cloudinary_id?: string;
  status_enum?: number;
  item_image_and_text_properties?: ItemImageAndTextPropertiesEnum;
  topping_content_type: 1 | 2 | 3;
}

export interface Product extends Modify<
  MenuItem<"2">,
  {
    sub_content: ToppingCategory[];
  }
> {
  is_18_plus: string;
  price: string;
  image_cloudinary_id?: string;
  allergen: number[];
  label: string[];
  recommended_menu_item_id?: number;
  status_enum: number;
  extended_media_type_enum: number[];
  extended_media_value: string[];
  type?: string;
  description?: string;
  note?: string;
  parent_id: number;
}

export enum VirtualItemTypes {
  "Normal" = 1,
  "Virtual" = 2,
  "MOST_SOLD" = 3,
}

export interface Category extends Modify<
  MenuItem<"1">,
  {
    sub_content: Product[];
    description?: string;
  }
> {
  image_cloudinary_id: string;
  virtual_item_type?: VirtualItemTypes;
}

export interface Screen {
  to?: string;
  name: string;
  description: string;
  language: number;
  show_address: boolean;
  show_category_bracket: boolean;
  show_public_order_number: boolean;
  font_family?: string;
  font_size?: number;
  minutes_in_advance_for_time_orders: number;
  apply_on_menu: boolean;
  apply_on_item_ids: number[];
  not_apply_on_item_ids: number[];
  is_active: boolean;
  item_id: number;
  show_price?: boolean;
}

export interface PincodeBody {
  old_pin_code: number;
  new_pin_code: number;
}

export interface AccountIdBody {
  account_id: number;
}

export interface MapSettingsResponse {
  is_auto_zoom_active: boolean;
  selected_order_statuses: number[];
  zoom_level?: number | null;
}

export interface MapScreenSettings {
  cluster_options: number;
  sort_options: number;
  statuses: number[];
  show_orders_assigned_to_courier: boolean;
  pin_code_check: boolean;
  item_id: number;
  merge_formulas: boolean;
  couriers_can_assign_orders_to_themselves: boolean;
}

export interface ProductDescription {
  gptResult: object;
  languages: { [key: string]: string };
}

export interface PayNLData {
  is_active: boolean;
  item_id: number;
  paynl_merchant_id: string;
  store_id: string;
  pos_terminal_name: string[];
  pos_terminal_id: string[];
  pos_terminal_user_id?: number | string | Array<number | string>;
}

export enum OrderSessionStatus {
  ACTIVE = 1,
  FINISHED = 2,
  WAITING_FOR_PAYMENT = 3,
  WAITING_FOR_WAITER = 4,
  CANCELLED = 5,
}

export interface OrdersSession {
  current_table_ids: number[];
  is_removed: boolean;
  item_id: number;
  order_ids: {
    [id: string]: number;
  };
  public_session_number: string;
  session_status: OrderSessionStatus;
  start_session_time: string;
  total_amount: number;
  payment_method: number;
  payment_status: number;
  tip: number;
}

export interface News {
  item_id: number;
  name: string;
  description: string;
  start_time: string;
  end_time: string;
  priority?: number;
  highlighted: boolean;
  is_active: boolean;
}

export interface AppsmenDashboard {
  [date: string]: AppsmenDashboardItem;
  all: AppsmenDashboardItem;
}

interface AppsmenDashboardItem {
  total_income: number;
  total_quantity: number;
  total_value: number;
  transaction_per_delivery_method_total_income: {
    [K in keyof typeof DeliveryMethods]: {
      [K in keyof typeof OrderTransactionPrices]: number;
    };
  };
  transaction_per_delivery_method_total_quantity: {
    [K in keyof typeof DeliveryMethods]: {
      [K in keyof typeof OrderTransactionPrices]: number;
    };
  };
  transaction_per_delivery_method_total_value: {
    [K in keyof typeof DeliveryMethods]: {
      [K in keyof typeof OrderTransactionPrices]: number;
    };
  };
  transaction_per_source_total_income: {
    [K in keyof typeof Sources]: {
      [K in keyof typeof OrderTransactionPrices]: number;
    };
  };
  transaction_per_source_total_quantity: {
    [K in keyof typeof DeliveryMethods]: {
      [K in keyof typeof OrderTransactionPrices]: number;
    };
  };
  transaction_per_source_total_value: {
    [K in keyof typeof DeliveryMethods]: {
      [K in keyof typeof OrderTransactionPrices]: number;
    };
  };
  transaction_total_income: {
    [K in keyof typeof OrderTransactionPrices]: number;
  };
  transaction_total_quantity: {
    [K in keyof typeof OrderTransactionPrices]: number;
  };
  transaction_total_value: {
    [K in keyof typeof OrderTransactionPrices]: number;
  };
}

export enum ItemImageAndTextPropertiesEnum {
  image_and_text = "0",
  only_text = "1",
  only_image = "2",
}
export interface CashDroSettings {
  is_active: boolean;
  ip: string;
  user_name: string;
  password: string;
  pos_name: string;
}
